import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core'
import { ContentState } from '@core/utils/route-utils'
import { FormGroup } from '@angular/forms'
import { CertificateHolderApiResponse, CertificateHolderForm } from '@features/certificate-holder/models/certificate-holder.model'
import { ActivatedRoute, Router } from '@angular/router'
import { GlobalErrorService } from '@services/global-error.service'
import { ModalHelperService } from '@services/modal-helper.service'
import { CertificateHolderService } from '@features/certificate-holder/services/certificate-holder.service'
import { CertificateHolderFormService } from '@features/certificate-holder/services/certificate-holder-form.service'
import { CertificateHolderStateService } from '@app/layout/sidebar/services/certificate-holder-state.service'
import { Observable, of, take, tap } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Injectable()
export class CertificateHolderManagerService {
  public readonly contentState = signal<ContentState>('none')
  public readonly isReadOnly = signal(false)
  public readonly certificateHolderForm: FormGroup<CertificateHolderForm>

  // Computed Signals for derived state
  public readonly isViewMode = computed(() => this.contentState() === 'view')
  public readonly isEditMode = computed(() => this.contentState() === 'edit')
  public readonly isCreateMode = computed(() => this.contentState() === 'create')
  public readonly formService = inject(CertificateHolderFormService)
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)
  private readonly certificateHolderService = inject(CertificateHolderService)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly modalHelper = inject(ModalHelperService)
  private readonly certificateHolderState = inject(CertificateHolderStateService)

  constructor() {
    this.certificateHolderForm = this.formService.buildForm()
    this.loadInitialData()

  }

  public loadCertificateHolder(uuid: string): void {
    this.certificateHolderService.getById(uuid).pipe().subscribe({
      next: data => {
        this.formService.populateFormArray(this.certificateHolderForm, data.recipientAddresses)
        this.certificateHolderForm.patchValue(data)
        this.certificateHolderForm.markAsPristine()
        this.formService.addAsyncValidators(this.certificateHolderForm, data.companyName, data.country, data.freetext)
      },
      error: () => this.globalErrorService.reportError({
        subsystemName: 'Certificate Holder Loading',
        description: 'The Certificate Holder could not be loaded.'
      })
    })
  }

  public saveCertificateHolder(): void {
    if (this.certificateHolderForm.invalid) return

    this.performSave$().subscribe({
      error: () => this.reportSaveError()
    })
  }

  public performSave$(): Observable<CertificateHolderApiResponse> {
    const rawValue = this.certificateHolderForm.getRawValue()

    const data: CertificateHolderApiResponse = {
      uuid: rawValue.uuid,
      companyName: rawValue.companyName ?? '',
      country: rawValue.country ?? '',
      freetext: rawValue.freetext ?? '',
      usedIn: rawValue.usedIn ?? [],
      recipientAddresses: rawValue.recipientAddresses.filter((address) => address.email?.trim() !== '') ?? [],
    }

    return this.certificateHolderService.save(data).pipe(
      take(1),
      tap(res => {
        this.certificateHolderForm.patchValue(res)
        this.certificateHolderForm.markAsPristine()
        if ((typeof data.uuid !== 'string' || data.uuid === '') && (typeof res.uuid === 'string' && res.uuid !== '')) {
          this.formService.addAsyncValidators(this.certificateHolderForm, res.companyName, res.country, res.freetext)
          this.certificateHolderState.reload().subscribe()
          this.isReadOnly.set(true)
          this.contentState.set('view')
          void this.router.navigate(['/certificate-holder', res.uuid])
        } else {
          this.triggerPageReload()
        }
      })
    )
  }

  public cancel(): void {
    if (this.isCreateMode()) {
      void this.router.navigate(['/certificate-holder'])
    } else {
      const uuid = this.certificateHolderForm.controls.uuid.value
      if (uuid !== undefined && uuid !== null && uuid !== '') {
        this.isReadOnly.set(true)
        this.contentState.set('view')
        this.loadCertificateHolder(uuid)
      } else {
        void this.router.navigate(['/certificate-holder'])
      }
    }
  }

  public confirmLeave(): Observable<boolean> {
    return this.modalHelper.confirmLeave(() => {
      if (this.certificateHolderForm.invalid) {
        this.certificateHolderForm.markAllAsTouched()
        return of(false)
      }
      return this.performSave$()
    }, () => this.reportSaveError())
  }

  public triggerPageReload(): void {
    globalThis.location.reload()
  }

  public confirmDelete(): Observable<boolean> {
    return this.modalHelper.confirmDelete(
      'Delete Certificate Holder',
      `Are you sure you want to delete Certificate Holder ${this.certificateHolderForm.getRawValue().companyName}`
    )
  }

  public duplicate(): void {
    this.certificateHolderForm.patchValue({
      uuid: null,
      recipientAddresses: this.certificateHolderForm.controls.recipientAddresses.getRawValue().map(address => ({ ...address, uuid: null })),
      usedIn: []
    })
    this.formService.addAsyncValidators(this.certificateHolderForm)
    this.isReadOnly.set(false)
    this.contentState.set('create')
  }

  public close(): void {
    void this.router.navigate(['/certificate-holder'])
  }

  public delete(): void {
    this.confirmDelete().subscribe(confirmed => {
      if (confirmed) {
        this.certificateHolderService.delete(this.certificateHolderForm.getRawValue().uuid!).subscribe({
          next: () => {
            void this.router.navigate(['/certificate-holder'])
            this.certificateHolderState.reload().subscribe()
          },
          error: () => this.globalErrorService.reportError({
            subsystemName: 'Certificate Holder Deletion',
            description: 'Could not delete certificate Holder. Please try again.'
          })
        })
      }
    })
  }

  private reportSaveError(): void {
    this.globalErrorService.reportError({
      subsystemName: 'Certificate Holder Saving',
      description: 'The Certificate Holder could not be saved.'
    })
  }

  private loadInitialData(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const uuid = params['uuid'] as string | undefined
      if (typeof uuid === 'string' && uuid !== '') {
        this.isReadOnly.set(true)
        this.loadCertificateHolder(uuid)
      } else {
        this.isReadOnly.set(false)
        this.certificateHolderForm.reset()
        this.formService.addAsyncValidators(this.certificateHolderForm)
      }
    })
  }

}
